import { Datasets, LCTOnlyDatasets } from "../../../constants/enums"
import { formatDateTimeToDate } from "../../../utils/general/generalUtils"
import { LCT_YES } from "../hooks/devices/lct/postBodyGenerators"

// Assumed raw input type
interface IRawSearchResult {
    total_results: number
    query_results: number
    data: []
}

export interface IDemDatabaseSearchResult {
    databaseId: number
    deviceReference: string
    datePublished: string
    status: string
    manufacturer: string
    modelRef: string
    type: string
    phases: string
    maxDemand: number
    voltage: string | number
    hasActiveAlert: boolean
}

// Required output type
export interface IDemDatabaseSearchResults {
    totalResults: number
    pageSize: number
    data: IDemDatabaseSearchResult[]
}

export const fieldMapper = {
    databaseId: {
        [Datasets.EVAC]: "DeviceID",
        [Datasets.EVDC]: "DeviceID",
        [Datasets.HP]: "DeviceID",
        [Datasets.EVAC_DRAFT]: "DeviceID",
        [Datasets.EVDC_DRAFT]: "DeviceID",
        [Datasets.HP_DRAFT]: "DeviceID",
    },
    publishedDate: {
        [Datasets.EVAC]: "PublishedDate",
        [Datasets.EVDC]: "PublishedDate",
        [Datasets.HP]: "PublishedDate",
        [Datasets.EVAC_DRAFT]: "PublishedDate",
        [Datasets.EVDC_DRAFT]: "PublishedDate",
        [Datasets.HP_DRAFT]: "PublishedDate",
    },
    deviceReference: {
        [Datasets.EVAC]: "SystemReference",
        [Datasets.EVDC]: "SystemReference",
        [Datasets.HP]: "SystemReference",
        [Datasets.EVAC_DRAFT]: "SystemReference",
        [Datasets.EVDC_DRAFT]: "SystemReference",
        [Datasets.HP_DRAFT]: "SystemReference",
    },
    manufacturer: {
        [Datasets.EVAC]: "Make",
        [Datasets.EVDC]: "Make",
        [Datasets.HP]: "Make",
        [Datasets.EVAC_DRAFT]: "Make",
        [Datasets.EVDC_DRAFT]: "Make",
        [Datasets.HP_DRAFT]: "Make",
    },
    model: {
        [Datasets.EVAC]: "ModelReference",
        [Datasets.EVDC]: "ModelReference",
        [Datasets.HP]: "ModelReference",
        [Datasets.EVAC_DRAFT]: "ModelReference",
        [Datasets.EVDC_DRAFT]: "ModelReference",
        [Datasets.HP_DRAFT]: "ModelReference",
    },
    type: {
        [Datasets.EVAC]: "",
        [Datasets.EVDC]: "EVCPType",
        [Datasets.HP]: "HeatPumpType",
        [Datasets.EVAC_DRAFT]: "",
        [Datasets.EVDC_DRAFT]: "EVCPType",
        [Datasets.HP_DRAFT]: "HeatPumpType",
    },
    phases: {
        [Datasets.EVAC]: "Phases",
        [Datasets.EVDC]: "Phases",
        [Datasets.HP]: "Phases",
        [Datasets.EVAC_DRAFT]: "Phases",
        [Datasets.EVDC_DRAFT]: "Phases",
        [Datasets.HP_DRAFT]: "Phases",
    },
    maxDemand: {
        [Datasets.EVAC]: "MaxCurrentAmpPerPhase",
        [Datasets.EVDC]: "TotalEVCPMaxDemandCurrent",
        [Datasets.HP]: "TotalHPSysMaxDemandCurrent",
        [Datasets.EVAC_DRAFT]: "MaxCurrentAmpPerPhase",
        [Datasets.EVDC_DRAFT]: "TotalEVCPMaxDemandCurrent",
        [Datasets.HP_DRAFT]: "TotalHPSysMaxDemandCurrent",
    },
    voltage: {
        [Datasets.EVAC]: "Voltage",
        [Datasets.EVDC]: "Voltage",
        [Datasets.HP]: "Voltage",
        [Datasets.EVAC_DRAFT]: "Voltage",
        [Datasets.EVDC_DRAFT]: "Voltage",
        [Datasets.HP_DRAFT]: "Voltage",
    },
    hasActiveAlert: {
        [Datasets.EVAC]: "HasActiveAlert",
        [Datasets.EVDC]: "HasActiveAlert",
        [Datasets.HP]: "HasActiveAlert",
        [Datasets.EVAC_DRAFT]: "HasActiveAlert",
        [Datasets.EVDC_DRAFT]: "HasActiveAlert",
        [Datasets.HP_DRAFT]: "HasActiveAlert",
    },
    complianceStatus: {
        [Datasets.EVAC]: "ComplianceStatus",
        [Datasets.EVDC]: "Compliance_Status",
        [Datasets.HP]: "Compliance_Status",
        [Datasets.EVAC_DRAFT]: "ComplianceStatus",
        [Datasets.EVDC_DRAFT]: "Compliance_Status",
        [Datasets.HP_DRAFT]: "Compliance_Status",
    },
}

/**
 * Derives type for EV AC based on if it is V2G compliant or not.
 * @param {Record<string, string>} result - single search result
 * @returns {string} - the EVAC type (V2G or EV AC)
 */
const setEVACType = (result: Record<string, string>): string =>
    result.V2G_Capability === LCT_YES ? "V2G" : "EV AC"

export const searchDemandDevicesMapper = {
    /**
     * Map `searchDemandDevices` into UI data model, confirming validity of data
     * in process. This consolidates all LCT datasets into one data model.
     *
     * Note ev ac dataset does not have a type. As such, this field is hard coded
     * @param {unknown} response - axios client response
     * @param {LCTOnlyDatasets} dataset - the lct database enum being requested
     * @returns {IDemDatabaseSearchResults} - axios client response parsed into
     * UI data model format
     */
    map: (
        response: unknown,
        dataset: LCTOnlyDatasets
    ): IDemDatabaseSearchResults => {
        // Confirm response.data is array
        const resTypeGuarded = response as IRawSearchResult
        if (!Array.isArray(resTypeGuarded.data)) {
            throw Error()
        }
        // Parse response.data into UI data model
        const resultsFormatted = (
            resTypeGuarded.data as Record<string, string>[]
        ).map(
            (result): IDemDatabaseSearchResult => ({
                databaseId: result[
                    fieldMapper.databaseId[dataset]
                ] as unknown as number,
                deviceReference: result[fieldMapper.deviceReference[dataset]],
                datePublished: formatDateTimeToDate(
                    result[fieldMapper.publishedDate[dataset]]
                ),
                status: result[fieldMapper.complianceStatus[dataset]],
                manufacturer: result[fieldMapper.manufacturer[dataset]],
                modelRef: result[fieldMapper.model[dataset]],
                type:
                    dataset === LCTOnlyDatasets.EVAC
                        ? setEVACType(result)
                        : result[fieldMapper.type[dataset]],
                phases: result[fieldMapper.phases[dataset]],
                maxDemand: result[
                    fieldMapper.maxDemand[dataset]
                ] as unknown as number,
                voltage: result[fieldMapper.voltage[dataset]] as unknown as
                    | number
                    | string,
                hasActiveAlert:
                    String(result[fieldMapper.hasActiveAlert[dataset]]) ===
                    "true",
            })
        )
        return {
            totalResults: resTypeGuarded.total_results,
            pageSize: resTypeGuarded.query_results,
            data: resultsFormatted,
        }
    },
    /**
     * Merges output of paginated requests into one response object.
     * @param pageResults - list of api call responses per page.
     * @returns single output object with `data` field flattened into one array
     */
    mergePaginatedData: (
        pageResults: IDemDatabaseSearchResults[] | undefined
    ): IDemDatabaseSearchResults | undefined =>
        // Parses output of API calls into final response format.
        pageResults
            ? {
                  totalResults: pageResults[0].totalResults,
                  pageSize: pageResults[0].pageSize,
                  data: pageResults?.reduce(
                      (
                          acc: IDemDatabaseSearchResult[],
                          pageResult: IDemDatabaseSearchResults
                      ) => [...acc, ...pageResult.data],
                      []
                  ),
              }
            : undefined,
}
